/**
 * Motion tokens for components. Mirrors the easing + duration scale in
 * DESIGN.md and globals.css `@theme` so JS-driven animations (Framer
 * Motion, GSAP) snap to the same values as CSS transitions.
 *
 * Use these instead of inline `[0.22, 1, 0.36, 1]` arrays or magic
 * numbers like `0.55`. Three pieces win when motion is consistent:
 * cohesion (everything feels one product), polish (no off-rhythm
 * animation), and re-tune (one place to change the page's pace).
 */

/* ── Easing — cinematic ease-out family ─────────────────────────────── */

/** Default for entrances. The page's signature curve. */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
/** Layout shifts and small reveals. */
export const EASE_OUT_QUART = [0.25, 1, 0.5, 1] as const;
/** Hero apex / display copy. Slightly more deceleration. */
export const EASE_OUT_QUINT = [0.22, 1, 0.36, 1] as const;
/** Continuous loops (byte stream, wave). */
export const EASE_IN_OUT_CUBIC = [0.65, 0, 0.35, 1] as const;
/** GSAP equivalents (string form). */
export const GSAP_EASE = {
  outExpo: 'expo.out',
  outQuart: 'power3.out',
  outQuint: 'power4.out',
  inOutCubic: 'power2.inOut',
  outPower2: 'power2.out',
} as const;

/* ── Duration scale ─────────────────────────────────────────────────── */
/* Five canonical durations in seconds. Components round to nearest. */

export const DUR = {
  instant: 0.08,
  fast: 0.2,
  base: 0.4,
  slow: 0.8,
  slower: 1.4,
} as const;

/** Convenience: same scale in ms (for setTimeout / GSAP duration override). */
export const DUR_MS = {
  instant: 80,
  fast: 200,
  base: 400,
  slow: 800,
  slower: 1400,
} as const;
